import { useEffect, useState } from 'react'
import { TraceRegion } from '../lib/trace'
import { api, type ConsentText, type GateState } from '../api'
import { theme } from '../theme'
import { Button, Card, Heading, Page } from '../components/ui'
import { Loading } from '../components/States'
import { fromServer, t } from '../lib/i18n'

/**
 * Gate 2. Tick the box once and it never comes back — unless the agreement
 * itself changes, because what is stored is the version number it was written
 * against, not a yes.
 *
 * The agreement's words come from the server, in English, and are translated
 * here like any other line (round 14). Changing a word on the server shows
 * that point in English until the dictionaries are updated; the test in
 * i18n.test.ts lists them, so it fails first.
 *
 * **Email plus** (29 Oct 2026, services/consent.py): for a family the server
 * asks by email, the tick sends the consent email instead, and this page
 * then says "check your email" until the parent has pressed Yes on the page
 * the email opens. Meanwhile a child can try Word Sprout on this device, with
 * nothing sent (TryIt.tsx), if that is switched on.
 */
export function Consent({
  onDone, gate, onSignOut, onTry,
}: { onDone: () => void; gate?: GateState | null; onSignOut?: () => void; onTry?: () => void }) {
  const [text, setText] = useState<ConsentText | null>(null)
  const [ticked, setTicked] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const byEmail = gate?.consent_method === 'email_plus'
  const pending = gate?.consent_pending ?? null

  useEffect(() => { api.consentText().then(setText).catch(() => setText(null)) }, [])

  if (!text) return <Loading what={t('Reading the agreement')} grownUp />

  const accept = async () => {
    setBusy(true)
    setProblem(null)
    try {
      await api.acceptConsent(text.version)
      onDone()
    } catch (err) {
      setProblem(fromServer(err, 'That did not work. Try again.'))
    } finally {
      setBusy(false)
    }
  }

  if (byEmail && pending) {
    return (
      <Page width={600}>
        <Heading sub={t('We sent a link to {email}. Open it, on any device, and press "Yes, I agree". Then come back here.', { email: pending.email })}>
          {t('Check your email')}
        </Heading>
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Button full onClick={onDone}>{t('I have agreed. Continue')}</Button>
            <Button full tone="quiet" onClick={() => void accept()} disabled={busy}>
              {t('Send the email again')}
            </Button>
            {problem ? <p style={{ margin: 0, color: theme.red, fontSize: 14 }}>{problem}</p> : null}
            <p style={{ margin: 0, fontSize: 14, color: theme.muted, lineHeight: 1.5 }}>
              {t('Not there? Look in spam or promotions. It comes from Word Sprout.')}
            </p>
          </div>
        </Card>
        {gate?.try_on_device && onTry ? (
          <Card style={{ marginTop: 14, background: theme.blueFaint }}>
            <p style={{ margin: '0 0 10px', fontSize: 15, color: theme.text, lineHeight: 1.5 }}>
              {t('While you wait, your child can try Word Sprout on this device. Nothing they do is sent to us until you agree.')}
            </p>
            <Button tone="quiet" onClick={onTry}>{t('Let my child try it')}</Button>
          </Card>
        ) : null}
        {onSignOut ? (
          <p style={{ marginTop: 14, textAlign: 'center' }}>
            <Button tone="ghost" onClick={onSignOut}>{t('Sign out')}</Button>
          </p>
        ) : null}
      </Page>
    )
  }

  return (
    <Page width={600}>
      <TraceRegion id="T1" writes note="agreeing saves consent_version and consented_at on parent">
      <Heading sub={t('Please read this once before your child starts.')}>
        {t(text.title)}
      </Heading>

      <Card>
        <ol style={{ margin: 0, paddingLeft: 22, color: theme.text2 }}>
          {text.points.map((point, i) => (
            <li key={i} style={{ marginBottom: 14, fontSize: 16, lineHeight: 1.55 }}>
              {t(point)}
            </li>
          ))}
        </ol>

        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 24,
          padding: 14, background: theme.blueFaint, borderRadius: 10, cursor: 'pointer',
        }}>
          <input
            type="checkbox"
            checked={ticked}
            onChange={(e) => setTicked(e.target.checked)}
            style={{ width: 22, height: 22, marginTop: 1, flexShrink: 0, cursor: 'pointer' }}
          />
          <span style={{ fontSize: 16, color: theme.text, fontWeight: 600, lineHeight: 1.4 }}>
            {t(text.tick_label)}
          </span>
        </label>

        <div style={{ marginTop: 20 }}>
          {/* Stays grey until the box is ticked. */}
          <Button full onClick={accept} disabled={!ticked || busy}>
            {busy ? t('Saving…') : byEmail ? t('Email me the consent link') : t('Continue')}
          </Button>
          {byEmail ? (
            <p style={{ margin: '10px 0 0', fontSize: 14, color: theme.muted, lineHeight: 1.5 }}>
              {t("To be sure you are the child's parent or guardian, we ask for your consent by email. Nothing is collected from your child before you agree.")}
            </p>
          ) : null}
          {problem ? <p style={{ margin: '10px 0 0', color: theme.red, fontSize: 14 }}>{problem}</p> : null}
        </div>
      </Card>

      <p style={{ marginTop: 14, color: theme.faint, fontSize: 13, textAlign: 'center' }}>
        {t('Version {v}. If this text ever changes, we will ask again.', { v: text.version })}
      </p>
      </TraceRegion>
    </Page>
  )
}
